import "./Stage0.css";
import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SlotMachine from "./slot machine/SlotMachine.jsx";
import Stage0LifeMeterUI from "./Stage0LifeMeterUI.jsx";

export default function Stage0() {
  const [playerMode, setPlayerMode] = useState("plant");

  const VOID_VIDEO = "/stageAssets/stage0/Stage0-Start-TheVoid-PRT.webm";

  const SYSTEMS_LOADING_VIDEO =
    playerMode === "plant"
      ? "/stageAssets/stage0/Systems Loading Background-Plant-PRT.webm"
      : "/stageAssets/stage0/Systems Loading Background-Human-PRT.webm";

  // audio paths
  const ZEN_CHIME_SRC = "/audio/Zen-Bell-Single-Chime.mp3"; 
  const FOREST_DAY_SRC = "/stageAssets/stage1-plant/audio/forest-day.mp3";

  const navigate = useNavigate();

  const [stage, setStage] = useState("void");
  const [needsTap, setNeedsTap] = useState(false);

  const voidRef = useRef(null);
  const systemsRef = useRef(null);

  useEffect(() => {
    const savedMode = localStorage.getItem("stage0PlayerMode");

    if (savedMode === "human") {
      setPlayerMode("human");
    } else {
      setPlayerMode("plant");
    }
  }, []);

  const handleVoidVideoEnd = useCallback(() => {
    setStage("slot");
  }, []);

  const handleSlotComplete = useCallback(() => {
    setStage("systems");
  }, []);

  const playPlantTransitionAudio = useCallback(() => {
  const zenChime = new Audio(ZEN_CHIME_SRC);
  const forestDay = new Audio(FOREST_DAY_SRC);

  zenChime.volume = 0.85;
  forestDay.volume = 0.35;

  zenChime.play().catch(() => {});
  forestDay.play().catch(() => {});

  // fade out zen chime faster
  const fadeDuration = 2500; // 2.5 seconds total fade
  const fadeInterval = 100; // update every 100ms
  const fadeStep = zenChime.volume / (fadeDuration / fadeInterval);

  const fadeOut = setInterval(() => {
      if (zenChime.volume > fadeStep) {
        zenChime.volume -= fadeStep;
      } else {
        zenChime.volume = 0;
        zenChime.pause();
        zenChime.currentTime = 0;
        clearInterval(fadeOut);
      }
    }, fadeInterval);
  }, []);

  const handleSystemsVideoEnd = useCallback(() => {
    if (playerMode === "plant") {
      playPlantTransitionAudio();
      navigate("/Stage1-Plant");
    } else {
      navigate("/Stage1");
    }
  }, [navigate, playerMode, playPlantTransitionAudio]);

  useEffect(() => {
    const tryPlay = async (videoEl) => {
      if (!videoEl) return;

      videoEl.muted = false;

      try {
        if (videoEl.currentTime > 0) videoEl.currentTime = 0;

        const p = videoEl.play();
        if (p && typeof p.then === "function") await p;

        setNeedsTap(false);
      } catch (err) {
        setNeedsTap(true);
      }
    };

    if (stage === "void") {
      tryPlay(voidRef.current);
    } else if (stage === "systems") {
      tryPlay(systemsRef.current);
    } else {
      setNeedsTap(false);
    }
  }, [stage]);

  const handleUserTapStart = async () => {
    const videoEl =
      stage === "void"
        ? voidRef.current
        : stage === "systems"
        ? systemsRef.current
        : null;

    if (!videoEl) return;

    try {
      videoEl.muted = true;
      await videoEl.play();
      setNeedsTap(false);
    } catch (e) {
      setNeedsTap(true);
    }
  };

  return (
    <div className="stage-container">
      {stage === "void" && (
        <video
          ref={voidRef}
          className="full-screen-video"
          autoPlay
          muted
          playsInline
          preload="auto"
          onEnded={handleVoidVideoEnd}
        >
          <source src={VOID_VIDEO} type="video/webm" />
        </video>
      )}

      {stage === "slot" && (
        <SlotMachine onComplete={handleSlotComplete} playerMode={playerMode} />
      )}

      {stage === "systems" && (
        <>
          <video
            ref={systemsRef}
            className="full-screen-video"
            autoPlay
            playsInline
            preload="auto"
            onEnded={handleSystemsVideoEnd}
          >
            <source src={SYSTEMS_LOADING_VIDEO} type="video/webm" />
          </video>

          <Stage0LifeMeterUI playerMode={playerMode} />
        </>
      )}

      {needsTap && (
        <button className="tap-to-start-overlay" onClick={handleUserTapStart}>
          Tap to Start
        </button>
      )}
    </div>
  );
}